// Shared bits for the quote-to-cash pages (quotes, orders, invoices).

export const usd = (n, dp = 2) => (n == null || Number.isNaN(Number(n)) ? '—'
  : `${Number(n) < 0 ? '-' : ''}$${Math.abs(Number(n)).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: Math.max(dp, 2) })}`);
export const fmtNum = (n, dp = 0, maxDp = dp) => (n == null ? '—' : Number(n).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: Math.max(dp, maxDp) }));

export const inputCls = 'w-full rounded-lg border border-[var(--border)] bg-white px-3 py-2 text-sm dark:bg-slate-800';
export const btnCls = 'rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white disabled:opacity-50';
export const btn2Cls = 'rounded-lg border border-[var(--border)] bg-white px-3 py-2 text-xs font-medium disabled:opacity-50 dark:bg-slate-800';

const CHIP = {
  draft: 'bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-slate-300',
  approved: 'bg-indigo-100 text-indigo-800',
  sending: 'bg-indigo-100 text-indigo-800',
  sent: 'bg-blue-100 text-blue-800',
  viewed: 'bg-amber-100 text-amber-800',
  accepted: 'bg-green-100 text-green-800',
  declined: 'bg-red-100 text-red-700',
  expired: 'bg-orange-100 text-orange-800',
  superseded: 'bg-gray-200 text-gray-600',
  void: 'bg-gray-200 text-gray-500 line-through',
  open: 'bg-blue-100 text-blue-800',
  in_fulfilment: 'bg-amber-100 text-amber-800',
  delivered: 'bg-green-100 text-green-800',
  closed: 'bg-gray-200 text-gray-600',
  cancelled: 'bg-gray-200 text-gray-500 line-through',
  partially_paid: 'bg-amber-100 text-amber-800',
  paid: 'bg-green-100 text-green-800',
  overdue: 'bg-red-100 text-red-700',
  disputed: 'bg-purple-100 text-purple-800',
};

export function StatusChip({ status }) {
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${CHIP[status] || CHIP.draft}`}>{String(status || '').replace('_', ' ')}</span>;
}

export function ErrorBox({ msg }) {
  return msg ? <div className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">{msg}</div> : null;
}

export function Field({ label, value, children }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wide text-[var(--text-secondary)]">{label}</p>
      <div className="break-words text-sm">{children ?? value ?? '—'}</div>
    </div>
  );
}

export function Labeled({ t, children, wide }) {
  return <label className={`block text-xs text-[var(--text-secondary)] ${wide ? 'sm:col-span-2' : ''}`}><span className="mb-1 block">{t}</span>{children}</label>;
}

// Open authenticated documents without navigating the portal tab.
export { openAuthed } from './openAuthed.mjs';

/**
 * a × b rounded to cents exactly like the server (modules/q2c/common.js mulCents): decimal
 * arithmetic, half away from zero, so 1 × 1.005 = 1.01 here too.
 */
/** Parse a typed number the way the server does ("205,000", "$1.77", " 12 "). NaN if not a number. */
export function parseNum(x) {
  const n = typeof x === 'number' ? x : (() => {
    const str = String(x ?? '').replace(/[$,\s]/g, '');
    return /^-?(\d+\.?\d*|\.\d+)(e[+-]?\d+)?$/i.test(str) ? Number(str) : NaN;
  })();
  // Out-of-range values are "not a number" here (the server rejects them with a message).
  return Number.isFinite(n) && Math.abs(n) < 1e12 ? n : NaN;
}

export function mulCents(a, b) {
  const dec = (x) => {
    const v = parseNum(x);
    if (!Number.isFinite(v)) return null;
    let str = String(Math.abs(v));
    if (str.includes('e')) str = Math.abs(v).toFixed(20).replace(/0+$/, '').replace(/\.$/, '');
    const [i, f = ''] = str.split('.');
    return { n: BigInt(`${i}${f}`) * (v < 0 ? -1n : 1n), scale: f.length };
  };
  let x; let y;
  try { x = dec(a); y = dec(b); } catch { return 0; }
  if (!x || !y) return 0;
  const prod = x.n * y.n;
  const shift = x.scale + y.scale - 2;
  if (shift <= 0) return Number(prod * 10n ** BigInt(-shift)) / 100;
  const div = 10n ** BigInt(shift);
  let q = prod / div;
  const r = prod % div;
  if ((r < 0n ? -r : r) * 2n >= div) q += prod < 0n ? -1n : 1n;
  return Number(q) / 100;
}
